import { dropUnansweredRequestEvent } from '@flashka/shared/api';

import { isIndexedDbEnvironmentError } from '../sync/web/idb-errors';
import { isChunkLoadError } from './chunk-load-error';

/**
 * Errors thrown by code that is not ours and that we cannot change: globals an
 * in-app browser or a browser extension injects into every page. Each entry
 * names the host that throws it — keep it that specific, so a real bug never
 * matches by accident.
 */
export const IGNORED_ERRORS: (string | RegExp)[] = [
  // Facebook / Instagram Android in-app browser: its injected performance
  // logger posts to a Java bridge that is already torn down (FLASHKA-VITE-1NM).
  'Java object is gone',
  // iOS in-app browsers (Chrome iOS, Google app) posting to a closed WKWebView.
  'The WKWebView was deallocated before the message was delivered',
  // Browser extensions calling into a tab that went away (FLASHKA-VITE-Z).
  'Invalid call to runtime.sendMessage(). Tab not found.',
  // Globals injected by the Zalo and iOS Google-app webviews and by extensions
  // (FLASHKA-VITE-TC, -119, -2TG, -163). Nothing in this repo defines them.
  /Can't find variable: (zaloJSV2|SCDynimacBridge)/,
  /^extDomain is not defined$/,
  // Scripts Chrome for iOS and the Google iOS app inject into the page throw
  // these minified messages (FLASHKA-VITE-2BW, -2D2: same users, same
  // second). With a stack their frames are the page URL and `allowUrls` drops
  // them (-2R6, -2RJ, -2S9, -2QC, -2QR, -2TH); these arrive with no frame at
  // all, or only `[native code]` ones, which `allowUrls` cannot judge.
  /^La$/,
  /^Ka`prod$/,
  // An injected WebKit script (Safari and Chrome iOS) with no source URL
  // (FLASHKA-VITE-15P). Nothing in this repo or its bundle names EmptyRanges.
  /^Can't find variable: EmptyRanges$/,
  // Benign: the browser postponed a resize notification by one frame.
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

/**
 * Report only errors whose top frame is OUR bundle. Third-party scripts on the
 * page — the Featurebase widget (`/js/sdk.js`, its dash.js loader), the TikTok
 * pixel (`/i18n/pixel/events.js`), Facebook's `iabjs://` injections — throw
 * into the same `window.onerror` (FLASHKA-VITE-1S9, -1SJ, -A, -2G5). Built
 * assets are served from `<origin>/assets/` on production and every preview,
 * so the page's own origin is the boundary. Events with no stack frames at all
 * are still sent: Sentry only applies `allowUrls` to a frame it has.
 *
 * Production builds only — the dev server serves `/src/**` and `/node_modules/**`.
 */
export function ownBundleUrls(origin: string): RegExp[] {
  const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return [new RegExp(`^${escaped}/assets/`)];
}

/**
 * Where the browser sends `sentry-trace` / `baggage`: our own same-origin
 * `/api`, nothing else (`@flashka/shared/api`, shared with mobile). `/api` is
 * same-origin on production and every preview (`getWebApiOrigin`), and must be
 * in dev too: the API sends no CORS headers.
 */
export { apiTracePropagationTargets } from '@flashka/shared/api';

/**
 * Sentry `beforeSend`: the shared unanswered-request filter, plus
 * - chunk-load failures the app recovers from by reloading (see
 *   ./chunk-load-error). A chunk failure that SURVIVED the reload is captured
 *   explicitly by the error boundary with `tags.chunkLoad = 'persistent'`, and
 *   passes;
 * - the browser's IndexedDB failing (Safari dropping its IDB server, a full
 *   disk, no IndexedDB at all — `isIndexedDbEnvironmentError`). The app now
 *   degrades on each (memory sync storage, an in-memory Yjs doc, a fallback
 *   live query) and tells the user once; the rate is the PostHog event
 *   `local_persistence_lost`. ~40 issues, none with a frame of ours to fix;
 * - an event whose every frame is `<anonymous>` (`hasOnlyAnonymousFrames`);
 * - Firefox's engine out-of-memory string (`isEngineOutOfMemory`);
 * - a known defect inside a bundled library, raised from the library's own
 *   callback with no frame of ours (`isKnownLibraryDefect`).
 *
 * A Dexie schema UPGRADE failure is the one IndexedDB failure that still
 * passes (`isSchemaUpgradeFailure`): the app degrades on it like on the others,
 * but it is our schema, not the device.
 */
export function beforeSendFilter<
  E extends { tags?: Record<string, unknown> } & FramedEvent,
>(event: E, hint: { originalException?: unknown } | undefined): E | null {
  const original = hint?.originalException;
  if (isChunkLoadError(original) && event.tags?.['chunkLoad'] !== 'persistent') {
    return null;
  }
  if (isIndexedDbEnvironmentError(original) && !isSchemaUpgradeFailure(original)) {
    return null;
  }
  if (isForgedRejectionEvent(original)) return null;
  if (isEngineOutOfMemory(original)) return null;
  if (hasOnlyAnonymousFrames(event)) return null;
  if (isKnownLibraryDefect(event)) return null;
  return dropUnansweredRequestEvent(event, hint);
}

type FramedEvent = {
  exception?: {
    values?: {
      value?: string;
      stacktrace?: { frames?: { filename?: string }[] };
    }[];
  };
};

/**
 * Dexie could not upgrade the database to the schema this build declares
 * (FLASHKA-VITE-1EQ: `DatabaseClosedError: UpgradeError Not yet support for
 * changing primary key` — a database last opened before v45 changed
 * `deckProperties` from `&name` to `&id`, or before v51 changed `quizProgress`
 * to `&[deckId+quizTs]`; Dexie cannot re-key a store in place). The session
 * still degrades to memory storage — `isIndexedDbEnvironmentError` is
 * unchanged — but the report is kept: it is a migration of ours to fix.
 */
export function isSchemaUpgradeFailure(value: unknown): boolean {
  let current: unknown = value;
  for (let depth = 0; depth < 4; depth++) {
    if (current === null || typeof current !== 'object') return false;
    const { name, message, inner } = current as {
      name?: unknown;
      message?: unknown;
      inner?: unknown;
    };
    if (name === 'UpgradeError') return true;
    if (typeof message === 'string' && message.startsWith('UpgradeError ')) return true;
    current = inner;
  }
  return false;
}

/**
 * SpiderMonkey reports a failed allocation by throwing the bare STRING
 * "out of memory" — no Error, no stack (FLASHKA-VITE-YS, -2TQ: Firefox, a
 * module preload failing with `payload: "out of memory"` two seconds after
 * the page loaded, before any code of ours ran). Only the exact string thrown
 * as the value: an Error of ours whose message says so is still reported, and
 * Chrome's / Safari's `RangeError`s (FLASHKA-VITE-1ZX) carry frames and stay.
 */
export function isEngineOutOfMemory(value: unknown): boolean {
  return value === 'out of memory';
}

/**
 * Defects inside a library we bundle, thrown from the library's OWN callback
 * (its ResizeObserver, its timer) with no frame of ours on the stack. Each
 * entry names the issue, the chunk the library builds into (vite.config.mts
 * `manualChunks`: `npm.<package>`) and the message — both must match. A
 * renamed chunk fails OPEN: the event is reported again.
 *
 * - @rive-app/canvas 2.37: its shared ResizeObserver tests `Map.get(...) !==
 *   null`, so an entry queued for a canvas removed in the same frame reads
 *   `undefined.onResize` (FLASHKA-VITE-2S5). A library bug; ours would need a
 *   patch of the package to change.
 * - @lottiefiles/dotlottie-web: a Rust panic in its wasm during its own
 *   debounced resize (`setup_sw_target`), and the aliasing error every later
 *   call on the poisoned player then throws (FLASHKA-VITE-2TR, -2TS: one
 *   iPhone, the same second).
 * - prosemirror-view, ONLY on a page Google Translate has rewritten: its
 *   DOMObserver reads the `<font>` wrappers Translate put inside the editor as
 *   an edit and resolves a position the document does not have — "Position 54
 *   out of range" from `readDOMChange` (FLASHKA-VITE-2TK: Chrome, the session
 *   clicking `font > font` inside the editor). Untranslated, the same throw
 *   would be a node view of ours corrupting the DOM, and is reported.
 */
type LibraryDefect = { chunk: RegExp; message: RegExp; when?: () => boolean };

const KNOWN_LIBRARY_DEFECTS: LibraryDefect[] = [
  { chunk: /\/npm\.-rive-app-canvas-[^/]*\.js$/, message: /\bonResize\b/ },
  {
    chunk: /\/npm\.-lottiefiles-dotlottie-web-[^/]*\.js$/,
    message:
      /^(Unreachable code should not be executed|unreachable\b)|^recursive use of an object detected which would lead to unsafe aliasing in rust$/,
  },
  {
    chunk: /\/npm\.prosemirror-(view|model)-[^/]*\.js$/,
    message: /^Position -?\d+ out of range$/,
    when: () => isPageMachineTranslated(),
  },
];

/**
 * Google Translate (Chrome's built-in one and the website widget) marks the
 * root element `translated-ltr` / `translated-rtl` while the page is
 * translated. Read at capture time, which is when it matters.
 */
export function isPageMachineTranslated(
  root: Element | undefined = typeof document === 'undefined'
    ? undefined
    : document.documentElement,
): boolean {
  return (
    !!root &&
    (root.classList.contains('translated-ltr') ||
      root.classList.contains('translated-rtl'))
  );
}

/** Frames that belong to no one's code: the engine's, and Sentry's own wrappers. */
const NEUTRAL_FRAME = /^\[native code\]$|\/npm\.-sentry-[^/]*\.js$/;

export function isKnownLibraryDefect(event: FramedEvent): boolean {
  const values = event.exception?.values ?? [];
  const thrown = values[values.length - 1];
  const message = thrown?.value;
  if (typeof message !== 'string') return false;
  const frames = values
    .flatMap((value) => value.stacktrace?.frames ?? [])
    .map((frame) => frame.filename ?? '')
    .filter((filename) => !NEUTRAL_FRAME.test(filename));
  if (frames.length === 0) return false;
  return KNOWN_LIBRARY_DEFECTS.some(
    ({ chunk, message: pattern, when }) =>
      pattern.test(message) &&
      frames.every((filename) => chunk.test(filename)) &&
      (!when || when()),
  );
}

/**
 * Every frame is `<anonymous>` — code with no script URL at all, i.e. an
 * extension's content script or something `eval`ed into the page
 * (FLASHKA-VITE-GX: "Maximum call stack size exceeded", Windows Chrome). Our
 * bundle never runs without a URL: each of its frames names `/assets/`. This is
 * the gap `allowUrls` leaves, because Sentry skips `<anonymous>` frames when
 * it looks for the URL to judge and then lets an event with none through.
 *
 * ALL frames, across every exception in the chain, and at least one: an event
 * with no frames, or with a single named frame, is not decided here.
 */
export function hasOnlyAnonymousFrames(event: FramedEvent): boolean {
  const frames = (event.exception?.values ?? []).flatMap(
    (value) => value.stacktrace?.frames ?? [],
  );
  return frames.length > 0 && frames.every((frame) => frame.filename === '<anonymous>');
}

/**
 * A script on the page dispatched its own `new CustomEvent('unhandledrejection')`
 * — `isTrusted: false`, no promise, no reason (FLASHKA-VITE-R: 639 events, 12
 * users; our bundle never dispatches one). A real rejection's event is trusted
 * and Sentry reports its `reason`, never the event itself.
 */
export function isForgedRejectionEvent(value: unknown): boolean {
  return (
    typeof Event !== 'undefined' &&
    value instanceof Event &&
    value.type === 'unhandledrejection' &&
    !value.isTrusted
  );
}

/**
 * The Sentry environment. The SDK defaults to `production` when none is set,
 * so until this existed a local dev server — and every E2E run against a local
 * build — reported AS production (`localhost:5173/src/...` frames,
 * mid-HMR `X is not defined`: FLASHKA-VITE-2TP, -1QF, -2W8, -2TV …).
 */
export function sentryEnvironment(isDev: boolean, buildEnvironment: string): string {
  return isDev ? 'development' : buildEnvironment;
}
